import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { verfahrenApi, type VerfahrenQueryParams } from '../api/verfahren';
import { restoreOne } from '../api/softDelete';
import { useSessionFilter } from '../hooks/useSessionFilter';
import { useCan } from '../hooks/useCan';
import { useColumnConfig, useModuleVisibility } from '../hooks/useConfigVisibility';
import { VERFAHREN_STATES, VERFAHREN_STATE_ORDER } from '../types';
import VerfahrenStateBadge from '../components/VerfahrenStateBadge';
import MergeDialog from '../components/MergeDialog';
import FilterDropdown from '../components/FilterDropdown';
import SortTh from '../components/SortTh';
import EmptyTableRow from '../components/EmptyTableRow';
import Pagination from '../components/Pagination';

export default function VerfahrenList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showMerge, setShowMerge] = useState(false);
  const [sortBy, setSortBy] = useSessionFilter<string>('verfahren_sortBy', 'created_at');
  const [sortDir, setSortDir] = useSessionFilter<'asc' | 'desc'>('verfahren_sortDir', 'desc');
  // Nur Filter persistieren, Seite immer bei 1 starten
  const [filterParams, setFilterParams] = useSessionFilter<Pick<VerfahrenQueryParams, 'search_text' | 'staat'>>(
    'verfahren_filter',
    {},
  );
  const [page, setPage] = useState(1);
  const [showHidden, setShowHidden] = useState(false);
  const canRestoreHidden = useCan('restore_hidden');
  const canRestoreOwn = useCan('restore_own');
  const cols = useColumnConfig('verfahren');
  const pv = useModuleVisibility();
  const params: Omit<VerfahrenQueryParams, 'sort_by' | 'sort_dir'> = { ...filterParams, page, per_page: 50, include_hidden: showHidden };

  const queryParams: VerfahrenQueryParams = {
    ...params,
    sort_by: sortBy,
    sort_dir: sortDir,
  };

  const { data, isLoading } = useQuery({
    queryKey: ['verfahren', queryParams],
    queryFn: () => verfahrenApi.getVerfahren(queryParams),
  });

  const restoreMutation = useMutation({
    mutationFn: (id: number) => restoreOne('verfahren', id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['verfahren'] }),
  });

  const { data: stats } = useQuery({
    queryKey: ['verfahren', 'stats'],
    queryFn: verfahrenApi.getStats,
  });

  const mergeMutation = useMutation({
    mutationFn: ({ targetId, sourceIds }: { targetId: number; sourceIds: number[] }) =>
      verfahrenApi.merge(targetId, sourceIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['verfahren'] });
      setSelectedIds(new Set());
      setShowMerge(false);
    },
  });

  const toggleSelection = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!data) return;
    const allIds = data.items.map((v) => v.id);
    const allSelected = allIds.every((id) => selectedIds.has(id));
    setSelectedIds(allSelected ? new Set() : new Set(allIds));
  };

  const setFilter = (key: keyof Pick<VerfahrenQueryParams, 'search_text' | 'staat'>, value: string | undefined) => {
    setFilterParams((prev) => ({ ...prev, [key]: value || undefined }));
    setPage(1);
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
    setPage(1);
  };

  const totalPages = data ? Math.ceil(data.total / (params.per_page || 50)) : 0;
  const isFilterActive = !!filterParams.staat || !!filterParams.search_text;

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Filter bar */}
        <div className="bg-white rounded-lg shadow p-4 mb-4 space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            <FilterDropdown label="Status" badgeCount={params.staat ? 1 : 0}>
              <div className="space-y-0.5">
                {VERFAHREN_STATE_ORDER.map((s) => {
                  const info = VERFAHREN_STATES[s];
                  const count = stats?.by_state?.[s] || 0;
                  return (
                    <button
                      key={s}
                      onClick={() => setFilter('staat', params.staat === s ? undefined : s)}
                      className={`w-full text-left px-3 py-1.5 rounded-lg text-sm flex items-center justify-between gap-3 ${
                        params.staat === s ? 'bg-gray-900 text-white' : 'hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <span>{info.label}</span>
                      <span className={`text-xs tabular-nums ${params.staat === s ? 'text-gray-300' : 'text-gray-400'}`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </FilterDropdown>

            <input
              type="text"
              placeholder="Suche nach Aktenzeichen, Notizen..."
              className="flex-1 min-w-48 border rounded-lg px-3 py-2 text-sm"
              value={params.search_text || ''}
              onChange={(e) => setFilter('search_text', e.target.value)}
            />

            {isFilterActive && (
              <button
                onClick={() => { setFilterParams({}); setPage(1); }}
                className="text-sm text-gray-500 hover:text-gray-700 whitespace-nowrap"
              >
                Filter zurücksetzen
              </button>
            )}

            {(canRestoreHidden || canRestoreOwn) && (
              <label className="inline-flex items-center gap-2 text-sm text-slate-700 whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={showHidden}
                  onChange={(e) => { setShowHidden(e.target.checked); setPage(1); }}
                />
                Ausgeblendete anzeigen
              </label>
            )}
          </div>

          {/* Active filter chips */}
          {params.staat && (
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 border border-blue-200 text-blue-800">
                Status: {VERFAHREN_STATES[params.staat]?.label}
                <button
                  onClick={() => setFilter('staat', undefined)}
                  className="text-blue-400 hover:text-blue-600 leading-none"
                >
                  ×
                </button>
              </span>
            </div>
          )}
        </div>

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center justify-between">
            <span className="text-sm font-medium text-blue-800">
              {selectedIds.size} Verfahren ausgewählt
            </span>
            <div className="flex items-center gap-2">
              {selectedIds.size >= 2 && (
                <button
                  onClick={() => setShowMerge(true)}
                  className="bg-purple-600 text-white px-3 py-1.5 rounded text-sm hover:bg-purple-700"
                >
                  Zusammenführen
                </button>
              )}
              <button
                onClick={() => setSelectedIds(new Set())}
                className="text-gray-600 hover:text-gray-800 text-sm"
              >
                Auswahl aufheben
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={!!data?.items?.length && data.items.every((v) => selectedIds.has(v.id))}
                        onChange={toggleSelectAll}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <SortTh field="aktenzeichen" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Aktenzeichen</SortTh>
                    {cols.status !== false && <SortTh field="staat" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Status</SortTh>}
                    {cols.vermieter !== false && pv.vermieter !== false && <SortTh field="vermieter_name" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Vermieter</SortTh>}
                    {cols.adresse !== false && pv.adressen !== false && <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Adresse</th>}
                    {cols.inserate !== false && pv.inserate !== false && <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Inserate</th>}
                    {cols.zustaendig !== false && <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Zuständig</th>}
                    {cols.frist !== false && <SortTh field="frist" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Frist</SortTh>}
                    <SortTh field="created_at" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Erstellt</SortTh>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data?.items?.map((v) => (
                    <tr
                      key={v.id}
                      className={`hover:bg-gray-50 cursor-pointer ${selectedIds.has(v.id) ? 'bg-blue-50' : ''} ${v.deleted_at ? 'bg-slate-100 opacity-60' : ''}`}
                      onClick={() => navigate(`/verfahren/${v.id}`)}
                    >
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(v.id)}
                          onChange={() => toggleSelection(v.id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-3 py-3 text-sm font-medium text-gray-900">
                        {v.aktenzeichen || <span className="text-gray-400 italic">Kein Az.</span>}
                      </td>
                      {cols.status !== false && (
                        <td className="px-3 py-3">
                          <VerfahrenStateBadge staat={v.staat} />
                        </td>
                      )}
                      {cols.vermieter !== false && pv.vermieter !== false && (
                        <td className="px-3 py-3 text-sm">
                          {v.vermieter_name ? (
                            <button
                              className="text-blue-600 hover:underline"
                              onClick={(e) => { e.stopPropagation(); navigate(`/vermieter/${v.vermieter_id}`); }}
                            >
                              {v.vermieter_name}
                            </button>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      )}
                      {cols.adresse !== false && pv.adressen !== false && (
                        <td className="px-3 py-3 text-sm text-gray-500">
                          {v.adresse_str ? (
                            <span>{v.adresse_str}{v.plz && `, ${v.plz}`}</span>
                          ) : '-'}
                        </td>
                      )}
                      {cols.inserate !== false && pv.inserate !== false && (
                        <td className="px-3 py-3 text-sm text-gray-500">
                          {v.inserate_count}
                        </td>
                      )}
                      {cols.zustaendig !== false && (
                        <td className="px-3 py-3 text-sm text-gray-500">
                          {v.zustaendig || '-'}
                        </td>
                      )}
                      {cols.frist !== false && (
                        <td className="px-3 py-3 text-sm text-gray-500">
                          {v.frist ? new Date(v.frist).toLocaleDateString('de-DE') : '-'}
                        </td>
                      )}
                      <td className="px-3 py-3 text-sm text-gray-500 whitespace-nowrap">
                        {v.deleted_at ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); restoreMutation.mutate(v.id); }}
                            className="text-green-700 hover:text-green-900 text-sm font-medium"
                          >
                            Wiederherstellen
                          </button>
                        ) : (
                          new Date(v.created_at).toLocaleDateString('de-DE')
                        )}
                      </td>
                    </tr>
                  ))}
                  {(!data?.items || data.items.length === 0) && (
                    <EmptyTableRow colSpan={9} message="Keine Verfahren gefunden." />
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              page={page}
              totalPages={totalPages}
              total={data?.total ?? 0}
              entityLabel="Verfahren"
              onPageChange={setPage}
            />
          </>
        )}
      </main>

      {/* Merge dialog */}
      {showMerge && (
        <MergeDialog
          selectedIds={[...selectedIds]}
          onMerge={(targetId, sourceIds) => mergeMutation.mutate({ targetId, sourceIds })}
          onClose={() => setShowMerge(false)}
          isPending={mergeMutation.isPending}
        />
      )}
    </div>
  );
}
